import { Component, OnInit, Inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { AnnouncementsService, Announcement, Template, ContentLinkTypeList, futureDateValidator, ContentLinkType } from '../../../../lib/announcements.service';
import { ModalService } from '../../../../lib/modal.service';
import { LogService } from '../../../../lib/log.service';
import { UserService } from 'app/lib/user.service';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
@Component({
    selector: 'app-announcement-edit-orgadmin',
    templateUrl: './announcement-edit-orgadmin.component.html',
    styleUrls: ['./announcement-edit-orgadmin.component.scss'],
    standalone: false
})
export class AnnouncementEditOrgadminComponent implements OnInit {
  announcementForm: FormGroup;
  templates: Template[] = [];
  isEditing = false;
  isNew = false;
  submitButtonPressed = false;
  config: any;
  linkOptions: string[] = ContentLinkTypeList;
  user: any;
  public Editor = ClassicEditor;
  constructor(
    private formBuilder: FormBuilder,
    private announcementsService: AnnouncementsService,
    private modalService: ModalService,
    private logService: LogService,
    private userService: UserService,
    public dialogRef: MatDialogRef<AnnouncementEditOrgadminComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this.user = this.userService.getUser();
    this.announcementForm = this.formBuilder.group({
        TemplateID: [null],
        Title: ['', Validators.required],
        Content: ['', Validators.required],
        Link: [''],
        StartDate: [null, Validators.required],
        EndDate: [null, Validators.required],
      });
      this.config = {
        toolbar: [
          'bold',
          'italic',
          'strikethrough',
          '|',
          'numberedList',
          'bulletedList',
          '|',
          'link',
          'pasteFromOffice'
        ]
      };
      this.data = data.data;
  }

  ngOnInit(): void {
    
    if (this.data?.announcementId) {
      if (this.data.isEditing) {
        this.isEditing = true;
      } else {
        this.isEditing = false;
      }
      this.loadAnnouncement(this.data.announcementId);
    }
    else {
      this.isNew = true;
    }
    this.loadTemplates();
  }

  loadTemplates(): void {
    this.announcementsService.getTemplateByOrgType(this.user.primaryOrganization.serviceType).subscribe(
      (templates) => {
        this.templates = templates;
        // Add "None" option at the beginning of the templates array
      },
      (error) => {
        this.logService.error(`Error loading templates: ${error}`);
      }
    );
  }

  loadAnnouncement(id: number): void {
    this.announcementsService.getAnnouncement(id).subscribe(
      (announcement) => {
        this.announcementForm.patchValue({
          TemplateID: announcement.TemplateID,
          Title: announcement.Title,
          Content: announcement.Content,
          Link: announcement.Link,
          StartDate: new Date(announcement.StartDate),
          EndDate: new Date(announcement.EndDate),
        });
      },
      (error) => {
        this.logService.error(`Error loading announcement: ${error}`);
        this.modalService.showAlert('Error', 'Failed to load announcement');
      }
    );
  }

  onTemplateSelect(templateId: number | null): void {
    if (templateId === null) {
      // None option selected - clear the form fields
      this.announcementForm.patchValue({
        Title: '',
        Content: '',
        Link: '',
      });
      return;
    }
    
    const template = this.templates.find(t => t.ID === templateId);
    if (template) {
      this.announcementForm.patchValue({
        Title: template.Title,
        Content: template.Content,
        Link: template.Link,
      });
    }
  }

  isViewMode(): boolean {
    return !this.isEditing && !this.isNew;
  }

  onSubmit(): void {
    this.submitButtonPressed = true;
    if (this.isViewMode()) {
        this.dialogRef.close();
        return;
      }
      if (this.announcementForm.valid) {
        const announcementData = this.announcementForm.value;
        announcementData.OrgID = this.user.primaryOrganization.id;
        
        if (this.isEditing && this.data?.announcementId) {
          this.announcementsService.updateAnnouncement(this.data.announcementId, announcementData).subscribe(
            () => {
              this.dialogRef.close(true);
              this.modalService.showAlert('Success', 'Announcement updated successfully');
            },
            (error) => {
              this.logService.error(`Error updating announcement: ${error}`);
              this.modalService.showAlert('Error', 'Failed to update announcement' + JSON.stringify(error));
            }
          );
        } else {
          this.announcementsService.createAnnouncement(announcementData).subscribe(
            () => {
              this.dialogRef.close(true);
              this.modalService.showAlert('Success', 'Announcement created successfully');
            },
            (error) => {
              this.logService.error(`Error creating announcement: ${error}`);
              this.modalService.showAlert('Error', 'Failed to create announcement' + JSON.stringify(error));
            }
          );
        }
      }
  }

  isStartDatePast(): boolean {
    const startDate = this.announcementForm.get('StartDate')?.value;
    if (!startDate) {
      return false;
    }
    const beginningOfToday = new Date();
    beginningOfToday.setHours(0, 0, 0, 0);
    return startDate < beginningOfToday;
  }

  minStartDate(): Date {
    if (this.isStartDatePast()) {
      return;
    }
    return new Date();
  }

  minEndDate(): Date {
    const startDate = this.announcementForm.get('StartDate')?.value;
    if (!startDate) return null;
    const minEndDate = new Date(startDate);
    minEndDate.setDate(minEndDate.getDate() + 1);
    return minEndDate;
  }

  startDateInvalid(): boolean {
    return !this.announcementForm.get('StartDate')?.disabled && this.announcementForm.get('StartDate')?.invalid && (this.announcementForm.get('StartDate')?.touched || this.submitButtonPressed);
  }

  onLinkSelect(link: string): void {
    const content = this.announcementForm.get('Content')?.value;
    if (link === ContentLinkType.NONE) {
        if (content.includes('Click <a href="">here</a> to begin!')) {
            this.announcementForm.patchValue({
                Content: content.replace('Click <a href="">here</a> to begin!', '')
            });
        }
    } else {
        if (!content.includes('Click <a href="">here</a> to begin!')) {
            this.announcementForm.patchValue({
                Content: content + 'Click <a href="">here</a> to begin!'
            });
        }
    }
  }

  onClose(): void {
    this.dialogRef.close();
  }
} 